import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

/*
 * Guía de diseño (claro / oscuro / alto contraste): sólo tokens de Fluent y variables --ichtys-*.
 * Lato 14 px (fontSizeBase300), radio 4 px (borderRadiusMedium), pastillas y filtros con radio
 * completo, encabezados de grilla en MAYÚSCULAS 12 bold, números tabulares a la derecha, acciones
 * a la derecha, fila seleccionada con fondo primario tenue y barra azul a la izquierda.
 */
const focusRing = {
  outlineStyle: 'solid',
  outlineWidth: tokens.strokeWidthThick,
  outlineColor: tokens.colorStrokeFocus2,
  outlineOffset: '2px',
};

export const useOfficesStyles = makeStyles({
  // --- Sección con toolbar + grilla -------------------------------------------------------------
  section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalS,
  },
  toolbarFilters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  toolbarActions: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
    marginLeft: 'auto',
  },
  search: {
    width: '280px',
    maxWidth: '100%',
  },
  filterDropdown: {
    minWidth: '180px',
    maxWidth: '100%',
  },
  // --- Pastillas con contador (filtros rápidos) ------------------------------------------------
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minHeight: '32px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': focusRing,
  },
  pillActive: {
    color: tokens.colorNeutralForegroundInverted,
    backgroundColor: tokens.colorNeutralBackgroundInverted,
    ...shorthands.borderColor(tokens.colorNeutralBackgroundInverted),
    ':hover': {
      backgroundColor: tokens.colorNeutralBackgroundInverted,
    },
  },
  pillDot: {
    width: '8px',
    height: '8px',
    flexShrink: 0,
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
  },
  dotSuccess: { backgroundColor: 'var(--ichtys-success)' },
  dotWarning: { backgroundColor: 'var(--ichtys-warning)' },
  dotBrand: { backgroundColor: 'var(--ichtys-primary)' },
  dotNeutral: { backgroundColor: tokens.colorNeutralForeground3 },
  pillCount: {
    minWidth: '20px',
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding('0', tokens.spacingHorizontalXS),
  },
  pillCountActive: {
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // --- Grilla ------------------------------------------------------------------------------------
  surface: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    overflowX: 'auto',
  },
  table: {
    minWidth: '720px',
  },
  headerCell: {
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.02em',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  row: {
    ':hover': {
      backgroundColor: 'var(--ichtys-row-hover-bg)',
    },
  },
  rowSelected: {
    backgroundColor: 'var(--ichtys-row-selected-bg)',
    boxShadow: 'inset 4px 0 0 0 var(--ichtys-row-selected-bar)',
    ':hover': {
      backgroundColor: 'var(--ichtys-row-selected-bg)',
    },
  },
  rowInactive: {
    color: tokens.colorNeutralForeground3,
  },
  primaryCell: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    minWidth: 0,
    ...shorthands.padding(tokens.spacingVerticalS, 0),
  },
  primaryText: {
    fontWeight: tokens.fontWeightBold,
    color: 'inherit',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  linkButton: {
    minWidth: 0,
    maxWidth: '100%',
    justifyContent: 'flex-start',
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: 'left',
    ...shorthands.padding(0),
    ':focus-visible': focusRing,
  },
  secondaryText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  numericCell: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  numericHeader: {
    textAlign: 'right',
    justifyContent: 'flex-end',
  },
  code: {
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  actionsCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
  },
  dangerItem: {
    color: tokens.colorPaletteRedForeground1,
  },
  // --- Estados vacío / carga ---------------------------------------------------------------------
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'dashed', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
  },
  emptyIcon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground2,
  },
  emptyTitle: {
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  emptyText: {
    maxWidth: '420px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  skeletonRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 96px',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  skeletonLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalXS,
    rowGap: tokens.spacingVerticalXS,
  },
});

/** Piezas propias de los formularios de Offices (el marco del drawer, campos y pie los pone GuideDrawer / GuideField). */
export const useOfficesFormStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    ...shorthands.padding(tokens.spacingVerticalS, 0),
  },
  readOnlyBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  roomList: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '280px',
    overflowY: 'auto',
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
  },
  roomOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
  },
  muted: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  twoColumns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalL,
    '@media (max-width: 760px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
});
